import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  GridValueGetterParams,
  GridColDef,
  GridActionsCellItem,
} from "@mui/x-data-grid";
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate, useParams } from "react-router-dom";
import Link from '@mui/material/Link';
import Typography from "@mui/material/Typography";
import Button from '@mui/material/Button/Button';
import Box from "@mui/material/Box/Box";
import AddIcon from '@mui/icons-material/Add';
import EditIcon1 from '@mui/icons-material/Edit';
// import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CrossIcon from '@mui/icons-material/Close';
import SecurityIcon from '@mui/icons-material/Security';

import {
  useAppDispatch,
  useAppSelector,
} from '../../store/hooks';

import CustomDataGrid from '../../components/DataGrid/DataGrid';
import BreadCrum from "../../components/Breadcrumbs/Breadcrumbs";
import { /* deleteUser,  */fetchUsers } from '../../store/userThunk';
import { /* IRole, */ IUserDetails } from "../../utils/user.types";
import { PermissionsEnum } from "../../utils/auth.types";
import { ApiStatus } from "../../utils/api.types";

import UserInviteDialog from "./UserInviteDialog";
import { Avatar, Card, CardContent, Grid, Dialog, DialogContent, DialogTitle, IconButton, Tooltip } from "@mui/material";
import UserEditDrawer from "./UserEditDrawer";
import UserPermissionsView from "../../components/Permissions/UserPermissionsView";
import CloseIcon from '@mui/icons-material/Close';

/**
 * Components to show users list in the data grid.
 */
const User = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { accountId = '' } = useParams();

  const { selectedAccount } = useAppSelector(state => state.auth);
  const { users, status } = useAppSelector(state => state.users);
  const { user, permissions } = useAppSelector(state => state.auth);

  const canCreateUsers = permissions?.includes(PermissionsEnum.Users_Create);
  // const canUpdateUsers = permissions?.includes(PermissionsEnum.Users_Update);
  const canViewUsers = permissions?.includes(PermissionsEnum.Users_View);
  // const canDeleteUsers = permissions?.includes(PermissionsEnum.Users_Delete);

  const [open, setOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [selectedUserForPermissions, setSelectedUserForPermissions] = useState<any>(null);

  /**
   * Fetch all the users list on user's page load.
   */
  useEffect(() => {
    // Fetch account related users if we are loading users on account details page.
    if (accountId) {
      dispatch(fetchUsers(accountId));
    } else if (selectedAccount) {
      const { _id: id } = selectedAccount;

      dispatch(fetchUsers(id));
    }
  }, [dispatch, selectedAccount, accountId, open]);



  /**
   * Method to open user Form.
   */
  const handleAddUser = () => {
    handleOpen();
  };

  /**
   * Handler for user name click.
   */
  const handleUserNameClick = useCallback((({ _id }: IUserDetails) => {
    if (accountId) {
      navigate(`/users/user-details/${_id}`, { state: { accountId } });
    } else {
      navigate(`/users/user-details/${_id}`);
    }
  }), [navigate, accountId]);

  /**
   * Method to handle delete user.
   */
  // const deleteSelectedUser = useCallback((user: IUserDetails) => () => {
  //   // Dispatch action to delete user.
  //   let userAccountId = accountId;
  //   if (!userAccountId) {
  //     userAccountId = selectedAccount._id;
  //   }
  //   dispatch(deleteUser({ userId: user._id, accountId: userAccountId }));
  // }, [dispatch, accountId, selectedAccount]);

  /**
   * Method to handle edit user.
   */
  const editSelectedUser = useCallback((userRow: any) => () => {
    let row = { ...userRow };
    row.accountId = selectedAccount._id;
    setSelectedUser(row);
    setDrawerOpen(true);
  }, [selectedAccount]);

  const drawerClose = () => { setDrawerOpen(false) };

  /**
   * Method to handle permissions dialog
   */
  const handleManagePermissions = useCallback((userRow: any) => () => {
    setSelectedUserForPermissions({
      _id: userRow._id,
      name: userRow.name,
      email: userRow.email
    });
    setPermissionsDialogOpen(true);
  }, []);

  const handleClosePermissions = () => {
    setPermissionsDialogOpen(false);
    setSelectedUserForPermissions(null);
  };

  const formatRoleText = (val: any) => {
    switch (val?.role) {
      case 'admin':
        return 'Admin';
      case 'superAdmin':
        return 'Super-Admin'
      case 'memeber':
        return 'Member'
      default:
        return val?.role;
    }
  }

  const checkIsUserVerifiedInAccount = (val: any) => {
    if (val?.length && val.length > 0) {
      return val[0].isUserVerified;
    }
    return false;
  };


  // Define Users data grid column defs.
  const columns: GridColDef[] = useMemo(() => [
    {
      field: "name",
      headerName: 'Name',
      type: "string",
      flex: 1.5,
      minWidth: 200,
      renderCell: ({ row, value }: GridValueGetterParams) => (
        <Tooltip title={value || ''} arrow placement="top">
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', overflow: 'hidden', width: '100%' }}
            component={Link}
            underline='hover'
            onClick={() => handleUserNameClick(row)}
          >
            <Avatar
              alt={value?.toUpperCase()}
              imgProps={{ "referrerPolicy": 'no-referrer' }}
              src={`${row.profilePic}`}
              sx={{ width: 36, height: 36, flexShrink: 0 }}
            />
            <Typography
              sx={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {value}
            </Typography>
          </Box>
        </Tooltip>
      )
    },
    {
      field: "email",
      headerName: 'Email',
      type: "string",
      flex: 1,
      renderCell: ({ value }: GridValueGetterParams) => (
        <Tooltip title={value || ''} arrow placement="top">
          <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {value}
          </Typography>
        </Tooltip>
      )
    },
    {
      field: "phone",
      headerName: 'Phone',
      type: "string",
      flex: 1
    },
    {
      field: "isEmailVerified",
      headerName: 'Email Verified',
      type: "string",
      renderCell: ({ row, value }: GridValueGetterParams) => (
        <div className="flex-column padIcon30">{value ? <CheckIcon /> : <CrossIcon />}</div>
      ),
      flex: 1
    },
    {
      field: "isActive",
      headerName: 'Is Active',
      type: "string",
      renderCell: ({ row, value }: GridValueGetterParams) => (
        <div className="flex-column padIcon memberEditCell">{value ? <CheckIcon /> : <CrossIcon />}</div>
      ),
      flex: 1
      // flex: 1,
      // valueGetter: ({ value }: GridValueGetterParams) => {
      //   return checkIsUserVerifiedInAccount(value)

      //}
    },

    // {
    //   field: 'accounts',
    //   headerName: 'Accounts',
    //   type: "singleSelect",
    //   flex: 1,
    //   valueGetter: ({ value }: GridValueGetterParams) => value[0]?.name ?? ''
    // },
    {
      field: 'role',
      headerName: 'Role',
      type: "singleSelect",
      flex: 1,
      valueGetter: ({ value }: GridValueGetterParams) => {
        return formatRoleText(value)
      }
    },
    {
      field: "status",
      headerName: 'Status',
      type: "string",
      flex: 0.8,
      renderCell: ({ row, value }: GridValueGetterParams) => {
        const role = row.useraccountroles.filter((r: any) => { return r.account === row.accounts[0]._id })
        return role && role[0] && role[0].isUserVerified ? 'Active' : 'Invited'
      },
    },
    {
      field: "accounts",
      headerName: 'Actions',
      type: "actions",
      flex: 0.8,
      getActions: (params: GridValueGetterParams) => {
        let isDisabled = params.row.accounts.filter((a: any) => a.owner === user._id && user._id === params.row._id).length > 0 ? true : false;
        const editAction = (
          <GridActionsCellItem
            disabled={isDisabled}
            icon={<EditIcon1 />}
            title="Edit"
            label="Edit"
            onClick={editSelectedUser(params.row)}
          />
        );
        const permissionsAction = (
          <GridActionsCellItem
            disabled={isDisabled}
            icon={<SecurityIcon />}
            title="Manage Permissions"
            label="Permissions"
            onClick={handleManagePermissions(params.row)}
          />
        );
        const actions: JSX.Element[] = [];
        actions.push(editAction);
        actions.push(permissionsAction);
        return actions;
      }

    }
    // {
    //   field: "actions",
    //   headerName: 'Actions',
    //   type: "actions",
    //   flex: 1,
    //   getActions: (params: GridValueGetterParams) => {
    //     const editAction = (
    //       <GridActionsCellItem
    //         icon={<EditIcon />}
    //         title="Edit"
    //         label="Edit"
    //         onClick={editSelectedUser(params.row)}
    //       />
    //     );

    //     const deleteAction = (
    //       <GridActionsCellItem
    //         icon={<DeleteIcon />}
    //         title="Delete"
    //         label="Delete"
    //         onClick={deleteSelectedUser(params.row)}
    //       />
    //     );

    //     const actions: JSX.Element[] = [];

    //     // Push edit action if user has update permission.
    //     if (canUpdateUsers && params.row._id !== user._id) {
    //       actions.push(editAction);
    //     }

    //     // Push edit action if user has delete permission.
    //     if (canDeleteUsers && params.row._id !== user._id) {
    //       actions.push(deleteAction);
    //     }

    //     return actions;
    //   }
    // }
  ], [
    // deleteSelectedUser,
    editSelectedUser,
    // canUpdateUsers,
    // canDeleteUsers,
    handleUserNameClick,
    handleManagePermissions,
    user
  ]);

  const handleClose = () => setOpen(false);
  const handleOpen = () => setOpen(true);
  // const onSubmit = (data: any) => {
  //   console.log(data);
  //   // Dispatch login action with email and password payload.
  //   // const { name, email, phone, password } = data;

  //   // dispatch(register({ name, email, phone, password }))
  // };

  return (
    <>
      {/* <BreadCrum /> */}
      <Card sx={{ border: '1px solid rgb(230, 235, 241)', borderRadius: '4px', boxShadow: 'none' }}>
        <CardContent sx={{ paddingTop: '16px' }}>

          <Box sx={{ display: "flex", justifyContent: "end", paddingTop: '8px', px: '24px' }}>
            {canCreateUsers && <Button
              startIcon={<AddIcon />}
              variant="contained"
              color='secondary'
              onClick={handleAddUser}
            >
              Add New Member
            </Button>}
          </Box>

          <Box sx={{ height: 350, width: "100%", marginTop: '24px' }}>
            {canViewUsers && <CustomDataGrid
              columns={columns}
              rows={users}
              getRowId={(row) => row._id}
              loading={status === ApiStatus.LOADING}
            />}
          </Box>
          <UserInviteDialog open={open} title="Member Invite" onClose={handleClose} />
          <UserEditDrawer userRow={selectedUser} open={drawerOpen} title="Change Member" onClose={drawerClose} />
          
          {/* Permissions Management Dialog */}
          <Dialog 
            open={permissionsDialogOpen} 
            onClose={handleClosePermissions}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              style: { minHeight: '70vh' }
            }}
          >
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                Manage User Permissions
                <IconButton onClick={handleClosePermissions}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {selectedUserForPermissions && (
                <UserPermissionsView 
                  user={selectedUserForPermissions}
                  onClose={handleClosePermissions}
                />
              )}
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    </>
  )
}

export default User;
